
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { Alert, Text } from 'braid-design-system';
import { IconTick } from 'braid-design-system';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "phase-4-managing-job-ads"
    }}>{`Phase 4: Managing job ads`}</h1>
    <p>{`The retired Job Posting API had a REST-based interface for managing SEEK job ads.
The SEEK API allows you to manage SEEK job ads through GraphQL queries and mutations.
The following documentation compares examples of each type of action (create, update, expire and get) in the Job Posting API with the equivalent in the SEEK API.`}</p>
    <h2 {...{
      "id": "recommendations-for-migration"
    }}>{`Recommendations for migration`}</h2>

    <Alert tone="caution" mdxType="Alert">
  <Text mdxType="Text">
    Any job ads posted via the Job Posting API cannot be managed by the SEEK API
    and vice versa. We recommend a progressive rollout, moving hirers onto the
    new integration with a clear distinction of job ads managed by the Job
    Posting API versus the SEEK API.
  </Text>
    </Alert>
    <p>{`There are two recommended ways of scaling usage of the SEEK API to all of your SEEK hirers.`}</p>
    <h3 {...{
      "id": "1-build-a-brand-new-integration"
    }}>{`1. Build a brand new integration`}</h3>
    <p>{`Recruitment software often has the ability to post job ads to multiple job boards at the same time for a single job requisition.
If this applies to your software, you can create an entirely new job board and integration for the SEEK API.
This also facilitates the creation of a new user experience to match up with any new value propositions within the SEEK API.`}</p>
    <h3 {...{
      "id": "2-use-the-seek-api-for-new-jobs"
    }}>{`2. Use the SEEK API for new jobs`}</h3>
    <p>{`If you would like to maintain your current integration patterns and migrate to use the SEEK API, build your new SEEK API integration to be run in parallel with your Job Posting API integration, not for a direct changeover.
New jobs will be posted and managed via the SEEK API, while older jobs will continue to use the Job Posting API integration until they are closed.
There is currently no method of migrating jobs posted via the Job Posting API to the SEEK API.`}</p>
    <h2 {...{
      "id": "data-structure-comparison"
    }}>{`Data structure comparison`}</h2>
    <p>{`This table compares the data structures of a job ad in the Job Posting API with the SEEK API `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{` object.`}</p>
    <p>{`Although most fields have equivalents in the SEEK API, their validation rules can diverge significantly.
For example, if a hirer isn’t configured to `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`link out`}</a>{` to an external apply form, the Job Posting API would silently ignore an `}<inlineCode parentName="p">{`applicationFormUrl`}</inlineCode>{` while the SEEK API will `}<a parentName="p" {...{
        "href": "/graphql/error-responses#bad_user_input"
      }}>{`respond with `}<inlineCode parentName="a">{`BAD_USER_INPUT`}</inlineCode></a>{`.
You should test your SEEK API integration to ensure it still handles invalid or conflicting inputs correctly.`}</p>

    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}><p parentName="th"><strong parentName="p">{`Job Posting API field`}</strong></p></th>
          <th parentName="tr" {...{
            "align": "center"
          }}><p parentName="th"><strong parentName="p">{`Deprecated`}</strong></p></th>
          <th parentName="tr" {...{
            "align": "left"
          }}><p parentName="th"><strong parentName="p"><a parentName="strong" {...{
                  "href": "/schema/named-type/PositionProfile"
                }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{` field`}</strong></p></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`id`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`profileId.value`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`thirdParties.advertiserId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionOrganizations[].id.value`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`thirdParties.agentId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`advertisementType`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`postingInstructions[].seekAnzAdvertisementType`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`jobTitle`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionTitle`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`searchJobTitle`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`location.id`}</inlineCode>{` + `}<inlineCode parentName="p">{`location.areaId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionLocation[].id.value`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`subclassificationId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`jobCategories[].id.value`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`workType`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`seekAnzWorkTypeCode`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`salary.type`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`offeredRemunerationPackage.basisCode`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`salary.minimum`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`offeredRemunerationPackage.ranges[].minimumAmount.value`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`salary.maximum`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`offeredRemunerationPackage.ranges[].maximumAmount.value`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`salary.details`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`offeredRemunerationPackage.descriptions`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`jobSummary`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionFormattedDescriptions[]`}</inlineCode>{` (of description `}<inlineCode parentName="p">{`SearchSummary`}</inlineCode>{`)`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`advertisementDetails`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionFormattedDescriptions[]`}</inlineCode>{` (of description `}<inlineCode parentName="p">{`AdvertisementDetails`}</inlineCode>{`)`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`contact`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Contact details will be set under `}<inlineCode parentName="p">{`positionOpening`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`video.url`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`seekVideo.url`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`video.position`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`seekVideo.position`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`applicationEmail`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Use `}<a parentName="p" {...{
                "href": "/use-cases/application-export"
              }}>{`Optimised Apply`}</a>{` to programmatically retrieve candidate applications`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`applicationFormUrl`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`postingInstructions[].applicationMethods[].applicationUri.url`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`endApplicationUrl`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`screenId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Use SEEK API `}<a parentName="p" {...{
                "href": "/use-cases/job-posting/questionnaires"
              }}>{`questionnaires`}</a></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`jobReference`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`seekHirerJobReference`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`agentJobReference`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`seekBillingReference`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`template`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`standout.logoId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Use `}<inlineCode parentName="p">{`postingInstructions[].brandingId`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`standout.brandingId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`postingInstructions[].brandingId`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`bullets`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionFormattedDescriptions[]`}</inlineCode>{` (of description `}<inlineCode parentName="p">{`SearchBulletPoint`}</inlineCode>{`)`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`recruiter.fullName`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionOpening.postingRequester.personContacts.name.formattedName`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`recruiter.email`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`positionOpening.postingRequester.personContacts.communication.email.address`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`recruiter.teamName`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`additionalProperties`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><ul><li><p><inlineCode parentName="td">{`Graduate`}</inlineCode>{`: Include seniority in your `}<inlineCode parentName="td">{`positionTitle`}</inlineCode>{` or `}<inlineCode parentName="td">{`positionFormattedDescriptions[]`}</inlineCode></p></li><li><p><inlineCode parentName="td">{`ResidentsOnly`}</inlineCode>{`: Include requirement in your `}<inlineCode parentName="td">{`positionFormattedDescriptions[]`}</inlineCode>{` or via SEEK API `}<a parentName="td" {...{
                    "href": "/use-cases/job-posting/questionnaires"
                  }}>{`questionnaires`}</a></p></li></ul></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`expiryDate`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`postingInstructions[].end`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`state`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><IconTick mdxType="IconTick" /></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`State is now based on `}<inlineCode parentName="p">{`postingInstructions`}</inlineCode>{` start and end dates.`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`creationId`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`postingInstructions[].idempotencyId`}</inlineCode>{`; see `}<a parentName="p" {...{
                "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad#idempotency"
              }}>{`idempotency`}</a></p></td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "migrating-endpoints-to-the-seek-api"
    }}>{`Migrating endpoints to the SEEK API`}</h2>
    <p>{`The SEEK API has a number of equivalent queries & mutations for posting and managing job ads.
The following examples assume your Job Posting API integration had already `}<a parentName="p" {...{
        "href": "/migration-guides/job-posting-api/phase-2-object-identifiers"
      }}>{`migrated to object identifiers`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><p parentName="th"><strong parentName="p">{`Description`}</strong></p></th>
          <th parentName="tr" {...{
            "align": null
          }}><p parentName="th"><strong parentName="p">{`Job Posting API endpoint`}</strong></p></th>
          <th parentName="tr" {...{
            "align": null
          }}><p parentName="th"><strong parentName="p">{`SEEK API operation`}</strong></p></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "#create-an-advertisement"
              }}>{`Create an advertisement`}</a></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><inlineCode parentName="p">{`POST`}</inlineCode>{` `}<inlineCode parentName="p">{`/advertisement`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/schema/mutation/postPosition"
              }}><inlineCode parentName="a">{`postPosition`}</inlineCode>{` mutation`}</a></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "#update-an-advertisement"
              }}>{`Update an advertisement`}</a></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><inlineCode parentName="p">{`PUT`}</inlineCode>{` `}<inlineCode parentName="p">{`/advertisement/<id>`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/schema/mutation/updatePostedPositionProfile"
              }}><inlineCode parentName="a">{`updatePostedPositionProfile`}</inlineCode>{` mutation`}</a></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "#expire-an-advertisement"
              }}>{`Expire an advertisement`}</a></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><inlineCode parentName="p">{`PATCH`}</inlineCode>{` `}<inlineCode parentName="p">{`/advertisement/<id>`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/schema/mutation/closePostedPositionProfile"
              }}><inlineCode parentName="a">{`closePostedPositionProfile`}</inlineCode>{` mutation`}</a></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "#get-an-advertisement"
              }}>{`Get an advertisement`}</a></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><inlineCode parentName="p">{`GET`}</inlineCode>{` `}<inlineCode parentName="p">{`/advertisement/<id>`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/schema/query/positionProfile"
              }}><inlineCode parentName="a">{`positionProfile`}</inlineCode>{` query`}</a></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "#get-all-advertisements"
              }}>{`Get all advertisements`}</a></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><inlineCode parentName="p">{`GET`}</inlineCode>{` `}<inlineCode parentName="p">{`/advertisement`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Deprecated. Use webhooks to receive events when positions are posted.`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "#get-all-advertisements-for-an-advertiser"
              }}>{`Get all advertisements for an advertiser`}</a></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><inlineCode parentName="p">{`GET`}</inlineCode>{` `}<inlineCode parentName="p">{`/advertisement?advertiserId=<id>`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/schema/query/positionOpenings"
              }}><inlineCode parentName="a">{`positionOpenings`}</inlineCode>{` query`}</a></p></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "create-an-advertisement"
    }}>{`Create an advertisement`}</h3>
    <p><strong parentName="p">{`Job Posting API`}</strong></p>
    <p><inlineCode parentName="p">{`POST`}</inlineCode>{` to `}<inlineCode parentName="p">{`/advertisement`}</inlineCode>{` with a body of the job ad contents.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http",
        "metastring": "label=\"Request\"",
        "label": "\"Request\""
      }}>{`POST /advertisement
Host: adposting-integration.cloud.seek.com.au

{
  "thirdParties": {
    "advertiserId": "seekAnzPublicTest:organization:seek:93WyyF1h"
  },
  "advertisementType": "Classic",
  "jobTitle": "Associate Developer",
  "location": {
    "id": "seekAnzPublicTest:location:seek:QVS62e6K"
  },
  "subclassificationId": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
  "workType": "PartTime",
  "salary": {
    "type": "HourlyRate",
    "minimum": 70,
    "maximum": 85
  },
  "jobSummary": "Start your software development career with a dynamic & growing team",
  "advertisementDetails": "A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area.",
  "contact": {
    "name": "Mary Manager",
    "phone": "03 8517 4100",
    "email": "mary.manager@example.com"
  },
  "creationId": "6eaeb58c-cc38-4b0e-8353-daa3011f1ece"
}
`}</code></pre>
    <p><strong parentName="p">{`SEEK API`}</strong></p>
    <p>{`GraphQL `}<a parentName="p" {...{
        "href": "/schema/mutation/postPosition"
      }}><inlineCode parentName="a">{`postPosition`}</inlineCode>{` mutation`}</a>{` with input of `}<a parentName="p" {...{
        "href": "/schema/named-type/PostPositionInput"
      }}><inlineCode parentName="a">{`PostPositionInput`}</inlineCode></a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "8517": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: PostPositionInput!) {\\n  postPosition(input: $input) {\\n    ... on PostPositionPayload_Success {\\n      positionOpening {\\n        documentId {\\n          value\\n        }\\n      }\\n      positionProfile {\\n        profileId {\\n          value\\n        }\\n      }\\n    }\\n    ... on PostPositionPayload_Conflict {\\n      conflictingPositionProfile {\\n        profileId {\\n          value\\n        }\\n      }\\n      conflictingPositionOpening {\\n        documentId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":157,\"column\":1,\"offset\":17284},\"end\":{\"line\":186,\"column\":4,\"offset\":17807},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"postingRequester\\\": {\\n        \\\"roleCode\\\": \\\"Company\\\",\\n        \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n        \\\"personContacts\\\": [\\n          {\\n            \\\"name\\\": {\\n              \\\"formattedName\\\": \\\"Mary Manager\\\"\\n            },\\n            \\\"roleCode\\\": \\\"HiringManager\\\",\\n            \\\"communication\\\": {\\n              \\\"phone\\\": [{ \\\"formattedNumber\\\": \\\"03 8517 4100\\\" }],\\n              \\\"email\\\": [\\n                {\\n                  \\\"address\\\": \\\"mary.manager@example.com\\\"\\n                }\\n              ]\\n            }\\n          }\\n        ]\\n      }\\n    },\\n    \\\"positionProfile\\\": {\\n      \\\"positionTitle\\\": \\\"Associate Developer\\\",\\n      \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n      \\\"positionFormattedDescriptions\\\": [\\n        {\\n          \\\"descriptionId\\\": \\\"SearchSummary\\\",\\n          \\\"content\\\": \\\"Start your software development career with a dynamic & growing team\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n          \\\"content\\\": \\\"A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area.\\\"\\n        }\\n      ],\\n      \\\"offeredRemunerationPackage\\\": {\\n        \\\"basisCode\\\": \\\"Hourly\\\",\\n        \\\"ranges\\\": {\\n          \\\"minimumAmount\\\": {\\n            \\\"value\\\": 70,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"maximumAmount\\\": {\\n            \\\"value\\\": 85,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"intervalCode\\\": \\\"Hour\\\"\\n        },\\n        \\\"descriptions\\\": []\\n      },\\n      \\\"seekAnzWorkTypeCode\\\": \\\"PartTime\\\",\\n      \\\"jobCategories\\\": \\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n      \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n      \\\"postingInstructions\\\": {\\n        \\\"seekAnzAdvertisementType\\\": \\\"Classic\\\",\\n        \\\"idempotencyId\\\": \\\"6eaeb58c-cc38-4b0e-8353-daa3011f1ece\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":188,\"column\":1,\"offset\":17809},\"end\":{\"line\":251,\"column\":4,\"offset\":19709},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "PostPositionInput!)": true,
        "{\\n": true,
        "": true,
        "postPosition(input:": true,
        "$input)": true,
        "...": true,
        "on": true,
        "PostPositionPayload_Success": true,
        "positionOpening": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "positionProfile": true,
        "profileId": true,
        "PostPositionPayload_Conflict": true,
        "conflictingPositionProfile": true,
        "conflictingPositionOpening": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":157,\"column\":1,\"offset\":17284},\"end\":{\"line\":186,\"column\":4,\"offset\":17807},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionOpening\\\":": true,
        "\\\"postingRequester\\\":": true,
        "\\\"roleCode\\\":": true,
        "\\\"Company\\\",\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"personContacts\\\":": true,
        "[\\n": true,
        "\\\"name\\\":": true,
        "\\\"formattedName\\\":": true,
        "\\\"Mary": true,
        "Manager\\\"\\n": true,
        "},\\n": true,
        "\\\"HiringManager\\\",\\n": true,
        "\\\"communication\\\":": true,
        "\\\"phone\\\":": true,
        "[{": true,
        "\\\"formattedNumber\\\":": true,
        "\\\"03": true,
        "4100\\\"": true,
        "}],\\n": true,
        "\\\"email\\\":": true,
        "\\\"address\\\":": true,
        "\\\"mary.manager@example.com\\\"\\n": true,
        "]\\n": true,
        "\\\"positionProfile\\\":": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Associate": true,
        "Developer\\\",\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "\\\"descriptionId\\\":": true,
        "\\\"SearchSummary\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"Start": true,
        "your": true,
        "software": true,
        "development": true,
        "career": true,
        "with": true,
        "a": true,
        "dynamic": true,
        "&": true,
        "growing": true,
        "team\\\"\\n": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"A": true,
        "newly": true,
        "created": true,
        "role": true,
        "for": true,
        "an": true,
        "ambitious": true,
        "<b>junior</b>": true,
        "or": true,
        "<b>graduate</b>": true,
        "developer.": true,
        "Be": true,
        "part": true,
        "of": true,
        "great": true,
        "team": true,
        "in": true,
        "the": true,
        "Bendigo": true,
        "area.\\\"\\n": true,
        "],\\n": true,
        "\\\"offeredRemunerationPackage\\\":": true,
        "\\\"basisCode\\\":": true,
        "\\\"Hourly\\\",\\n": true,
        "\\\"ranges\\\":": true,
        "\\\"minimumAmount\\\":": true,
        "\\\"value\\\":": true,
        "70,\\n": true,
        "\\\"currency\\\":": true,
        "\\\"AUD\\\"\\n": true,
        "\\\"maximumAmount\\\":": true,
        "85,\\n": true,
        "\\\"intervalCode\\\":": true,
        "\\\"Hour\\\"\\n": true,
        "\\\"descriptions\\\":": true,
        "[]\\n": true,
        "\\\"seekAnzWorkTypeCode\\\":": true,
        "\\\"PartTime\\\",\\n": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n": true,
        "\\\"postingInstructions\\\":": true,
        "\\\"seekAnzAdvertisementType\\\":": true,
        "\\\"Classic\\\",\\n": true,
        "\\\"idempotencyId\\\":": true,
        "\\\"6eaeb58c-cc38-4b0e-8353-daa3011f1ece\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":188,\"column\":1,\"offset\":17809},\"end\":{\"line\":251,\"column\":4,\"offset\":19709},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: PostPositionInput!) {
  postPosition(input: $input) {
    ... on PostPositionPayload_Success {
      positionOpening {
        documentId {
          value
        }
      }
      positionProfile {
        profileId {
          value
        }
      }
    }
    ... on PostPositionPayload_Conflict {
      conflictingPositionProfile {
        profileId {
          value
        }
      }
      conflictingPositionOpening {
        documentId {
          value
        }
      }
    }
  }
}
`}</code></pre>
    <p>{`For more general information, see the documentation on `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad"
      }}>{`posting a job ad`}</a>{` in the Job Posting use case.`}</p>
    <h3 {...{
      "id": "update-an-advertisement"
    }}>{`Update an advertisement`}</h3>
    <p><strong parentName="p">{`Job Posting API`}</strong></p>
    <p><inlineCode parentName="p">{`PUT`}</inlineCode>{` to `}<inlineCode parentName="p">{`/advertisement/{advertisementId}`}</inlineCode>{` with a body of the job ad contents.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http",
        "metastring": "label=\"Request\"",
        "label": "\"Request\""
      }}>{`PUT /advertisement/8e2fde50-bc5f-4a12-9cfb-812e50500184
Host: adposting-integration.cloud.seek.com.au

{
  "thirdParties": {
    "advertiserId": "seekAnzPublicTest:organization:seek:93WyyF1h"
  },
  "advertisementType": "Premium",
  "jobTitle": "Lead Developer",
  "location": "seekAnzPublicTest:location:seek:QVS62e6K",
  "subclassificationId": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
  "workType": "FullTime",
  "salary": {
    "type": "AnnualPackage",
    "minimum": 100000,
    "maximum": 130000,
    "details": "Up to $130k excluding super"
  },
  "jobSummary": "Experienced software developer wanted to lead an existing team.",
  "advertisementDetails": "<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>",
  "standout": {
    "bullets": [
      "Line management required",
      "Need experience with cloud deployments",
      "Competitive benefits packages"
    ]
  },
  "video": {
    "url": "https://www.youtube.com/embed/Q3UvigPHCjI",
    "position": "Above"
  }
}
`}</code></pre>
    <p><strong parentName="p">{`SEEK API`}</strong></p>
    <p>{`GraphQL `}<a parentName="p" {...{
        "href": "/schema/mutation/updatePostedPositionProfile"
      }}><inlineCode parentName="a">{`updatePostedPositionProfile`}</inlineCode>{` mutation`}</a>{` with input of `}<a parentName="p" {...{
        "href": "/schema/named-type/UpdatePostedPositionProfileInput"
      }}><inlineCode parentName="a">{`UpdatePostedPositionProfileInput`}</inlineCode></a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: UpdatePostedPositionProfileInput!) {\\n  updatePostedPositionProfile(input: $input) {\\n    positionProfile {\\n      profileId {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":304,\"column\":1,\"offset\":21478},\"end\":{\"line\":314,\"column\":4,\"offset\":21668},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionProfile\\\": {\\n      \\\"profileId\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\",\\n      \\\"positionTitle\\\": \\\"Lead Developer\\\",\\n      \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n      \\\"positionFormattedDescriptions\\\": [\\n        {\\n          \\\"descriptionId\\\": \\\"SearchSummary\\\",\\n          \\\"content\\\": \\\"Experienced software developer wanted to lead an existing team.\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Line management required\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Need experience with cloud deployments\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Competitive benefits packages\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n          \\\"content\\\": \\\"<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>\\\"\\n        }\\n      ],\\n      \\\"offeredRemunerationPackage\\\": {\\n        \\\"basisCode\\\": \\\"Salaried\\\",\\n        \\\"ranges\\\": {\\n          \\\"minimumAmount\\\": {\\n            \\\"value\\\": 100000,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"maximumAmount\\\": {\\n            \\\"value\\\": 130000,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"intervalCode\\\": \\\"Year\\\"\\n        },\\n        \\\"descriptions\\\": [\\\"Up to $130k excluding super\\\"]\\n      },\\n      \\\"seekAnzWorkTypeCode\\\": \\\"FullTime\\\",\\n      \\\"seekVideo\\\": {\\n        \\\"url\\\": \\\"https://www.youtube.com/embed/Q3UvigPHCjI\\\",\\n        \\\"seekAnzPositionCode\\\": \\\"Above\\\"\\n      },\\n      \\\"jobCategories\\\": \\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n      \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n      \\\"postingInstructions\\\": {\\n        \\\"seekAnzAdvertisementType\\\": \\\"Premium\\\"\\n        // `end` date is omitted so the existing end date will be preserved.\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":316,\"column\":1,\"offset\":21670},\"end\":{\"line\":374,\"column\":4,\"offset\":23685},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "UpdatePostedPositionProfileInput!)": true,
        "{\\n": true,
        "": true,
        "updatePostedPositionProfile(input:": true,
        "$input)": true,
        "positionProfile": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":304,\"column\":1,\"offset\":21478},\"end\":{\"line\":314,\"column\":4,\"offset\":21668},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionProfile\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\",\\n": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Lead": true,
        "Developer\\\",\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "[\\n": true,
        "\\\"descriptionId\\\":": true,
        "\\\"SearchSummary\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"Experienced": true,
        "software": true,
        "developer": true,
        "wanted": true,
        "to": true,
        "lead": true,
        "an": true,
        "existing": true,
        "team.\\\"\\n": true,
        "},\\n": true,
        "\\\"SearchBulletPoint\\\",\\n": true,
        "\\\"Line": true,
        "management": true,
        "required\\\"\\n": true,
        "\\\"Need": true,
        "experience": true,
        "with": true,
        "cloud": true,
        "deployments\\\"\\n": true,
        "\\\"Competitive": true,
        "benefits": true,
        "packages\\\"\\n": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"<p>Acme": true,
        "Inc": true,
        "is": true,
        "looking": true,
        "for": true,
        "experienced": true,
        "their": true,
        "transformation.</p><p>You": true,
        "should": true,
        "bring": true,
        "your": true,
        "passion": true,
        "both": true,
        "technology": true,
        "and": true,
        "people": true,
        "this": true,
        "exciting": true,
        "role.</p>\\\"\\n": true,
        "],\\n": true,
        "\\\"offeredRemunerationPackage\\\":": true,
        "\\\"basisCode\\\":": true,
        "\\\"Salaried\\\",\\n": true,
        "\\\"ranges\\\":": true,
        "\\\"minimumAmount\\\":": true,
        "\\\"value\\\":": true,
        "100000,\\n": true,
        "\\\"currency\\\":": true,
        "\\\"AUD\\\"\\n": true,
        "\\\"maximumAmount\\\":": true,
        "130000,\\n": true,
        "\\\"intervalCode\\\":": true,
        "\\\"Year\\\"\\n": true,
        "\\\"descriptions\\\":": true,
        "[\\\"Up": true,
        "$130k": true,
        "excluding": true,
        "super\\\"]\\n": true,
        "\\\"seekAnzWorkTypeCode\\\":": true,
        "\\\"FullTime\\\",\\n": true,
        "\\\"seekVideo\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://www.youtube.com/embed/Q3UvigPHCjI\\\",\\n": true,
        "\\\"seekAnzPositionCode\\\":": true,
        "\\\"Above\\\"\\n": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n": true,
        "\\\"postingInstructions\\\":": true,
        "\\\"seekAnzAdvertisementType\\\":": true,
        "\\\"Premium\\\"\\n": true,
        "//": true,
        "`end`": true,
        "date": true,
        "omitted": true,
        "so": true,
        "the": true,
        "end": true,
        "will": true,
        "be": true,
        "preserved.\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":316,\"column\":1,\"offset\":21670},\"end\":{\"line\":374,\"column\":4,\"offset\":23685},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: UpdatePostedPositionProfileInput!) {
  updatePostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
`}</code></pre>
    <p>{`For more general information, see the documentation on `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/updating-a-job-ad"
      }}>{`updating a job ad`}</a>{` in the Job Posting use case.`}</p>
    <h3 {...{
      "id": "expire-an-advertisement"
    }}>{`Expire an advertisement`}</h3>
    <p><strong parentName="p">{`Job Posting API`}</strong></p>
    <p><inlineCode parentName="p">{`PATCH`}</inlineCode>{` to `}<inlineCode parentName="p">{`/advertisement/{advertisementId}`}</inlineCode>{` with a body describing the field to modify.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http",
        "metastring": "label=\"Request\"",
        "label": "\"Request\""
      }}>{`PATCH /advertisement/8e2fde50-bc5f-4a12-9cfb-812e50500184
Host: adposting-integration.cloud.seek.com.au

[
  {
    "op": "replace",
    "path": "state",
    "value": "Expired"
  }
]
`}</code></pre>
    <p><strong parentName="p">{`SEEK API`}</strong></p>
    <p>{`The SEEK API supports automatically closing job ads on the `}<inlineCode parentName="p">{`end`}</inlineCode>{` date specified in their posting instructions.
This can be provided when `}<a parentName="p" {...{
        "href": "#create-an-advertisement"
      }}>{`posting`}</a>{` or `}<a parentName="p" {...{
        "href": "#update-an-advertisement"
      }}>{`updating`}</a>{` a job ad.
SEEK strongly recommends using automatic closure instead of scheduled events within your software.`}</p>
    <p>{`While the `}<a parentName="p" {...{
        "href": "/schema/mutation/updatePostedPositionProfile"
      }}><inlineCode parentName="a">{`updatePostedPositionProfile`}</inlineCode>{` mutation`}</a>{` mostly behaves like an HTTP PUT,
omitting the `}<a parentName="p" {...{
        "href": "/schema/named-type/UpdatePostingInstructionInput#/field/end"
      }}><inlineCode parentName="a">{`UpdatePostingInstructionInput.end`}</inlineCode></a>{` field will preserve the existing end date like an HTTP PATCH.
See the documentation on `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/updating-a-job-ad"
      }}>{`updating a job ad`}</a>{` in the Job Posting use case for more information.`}</p>
    <p>{`You can explicitly close job ads earlier using the GraphQL `}<a parentName="p" {...{
        "href": "/schema/mutation/closePostedPositionProfile"
      }}><inlineCode parentName="a">{`closePostedPositionProfile`}</inlineCode>{` mutation`}</a>{` with input of `}<a parentName="p" {...{
        "href": "/schema/named-type/ClosePostedPositionProfileInput"
      }}><inlineCode parentName="a">{`ClosePostedPositionProfileInput`}</inlineCode></a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: ClosePostedPositionProfileInput!) {\\n  closePostedPositionProfile(input: $input) {\\n    positionProfile {\\n      profileId {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":412,\"column\":1,\"offset\":25095},\"end\":{\"line\":422,\"column\":4,\"offset\":25283},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionProfile\\\": {\\n      \\\"profileId\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":424,\"column\":1,\"offset\":25285},\"end\":{\"line\":432,\"column\":4,\"offset\":25419},\"indent\":[1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "ClosePostedPositionProfileInput!)": true,
        "{\\n": true,
        "": true,
        "closePostedPositionProfile(input:": true,
        "$input)": true,
        "positionProfile": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":412,\"column\":1,\"offset\":25095},\"end\":{\"line\":422,\"column\":4,\"offset\":25283},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionProfile\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":424,\"column\":1,\"offset\":25285},\"end\":{\"line\":432,\"column\":4,\"offset\":25419},\"indent\":[1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: ClosePostedPositionProfileInput!) {
  closePostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
`}</code></pre>
    <p>{`For more general information, see the documentation on `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/closing-a-job-ad"
      }}>{`closing a job ad`}</a>{` in the Job Posting use case.`}</p>
    <h3 {...{
      "id": "get-an-advertisement"
    }}>{`Get an advertisement`}</h3>
    <p><strong parentName="p">{`Job Posting API`}</strong></p>
    <p><inlineCode parentName="p">{`GET`}</inlineCode>{` to `}<inlineCode parentName="p">{`/advertisement/{advertisementId}`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"http\",\"meta\":\"label=\\\"Request\\\"\",\"value\":\"GET /advertisement/1c855f8d-86a9-44f4-b7ab-250e01ff8a77\\nHost: adposting-integration.cloud.seek.com.au\",\"position\":{\"start\":{\"line\":449,\"column\":1,\"offset\":26147},\"end\":{\"line\":452,\"column\":4,\"offset\":26276},\"indent\":[1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Response\\\"\",\"value\":\"{\\n  // The SEEK API uses the object identifier in `profileId.value` to uniquely\\n  // identify job ads. However, these are distinct from the Job Posting API's\\n  // advertisement IDs.\\n  \\\"id\\\": \\\"1c855f8d-86a9-44f4-b7ab-250e01ff8a77\\\",\\n\\n  \\\"thirdParties\\\": {\\n    // => `positionOrganizations[].id.seekAnzAdvertiserId`\\n    \\\"advertiserId\\\": \\\"10000\\\"\\n  },\\n\\n  // => `positionOpening.postingRequester.personContacts[]`\\n  \\\"recruiter\\\": {\\n    // => `positionOpening.postingRequester.personContacts[].name.formattedName`\\n    \\\"fullName\\\": \\\"Lucy Flatley\\\",\\n\\n    // => `positionOpening.postingRequester.personContacts[].communication.email[].address`\\n    \\\"email\\\": \\\"Lucy_Flatley15@example.com\\\"\\n  },\\n\\n  // => `positionTitle`\\n  \\\"jobTitle\\\": \\\"Chief Assurance Analyst\\\",\\n\\n  // => `positionLocation`\\n  \\\"location\\\": {\\n    \\\"id\\\": \\\"Sydney\\\",\\n    \\\"areaId\\\": \\\"CBDInnerWestEasternSuburbs\\\"\\n  },\\n\\n  // => `jobCategories[]`\\n  // jobCategories requires an object identifier to specify a job category hierarchy.\\n  \\\"subclassificationId\\\": \\\"TestingQualityAssurance\\\",\\n\\n  // => `seekAnzWorkTypeCode`\\n  \\\"workType\\\": \\\"PartTime\\\",\\n\\n  // => `offeredRemunerationPackage`\\n  \\\"salary\\\": {\\n    // => `offeredRemunerationPackage.basisCode`\\n    \\\"type\\\": \\\"HourlyRate\\\",\\n\\n    // => `offeredRemunerationPackage.ranges[].minimumAmount.value`\\n    \\\"minimum\\\": 75,\\n\\n    // => `offeredRemunerationPackage.ranges[].maximumAmount.value`\\n    \\\"maximum\\\": 99.99,\\n\\n    // => `offeredRemunerationPackage.descriptions`\\n    \\\"details\\\": \\\"$75 - $100 per hour\\\"\\n  },\\n\\n  // => `positionFormattedDescriptions[]`\\n  // Formatted description should have a descriptionId.value of \\\"SearchSummary\\\"\\n  \\\"jobSummary\\\": \\\"Excellent opportunity for an assurance analyst in Australia\\\",\\n\\n  // => `positionFormattedDescriptions[]`\\n  // Formatted description should have a descriptionId.value of \\\"AdvertisementDetails\\\"\\n  \\\"advertisementDetails\\\": \\\"\\\\n<strong>About the role</strong>\\\\n<p>Ut necessitatibus amet deserunt laudantium veritatis. Sapiente occaecati\\\\nodio quae quod magnam autem temporibus error rerum. Id porro distinctio eaque\\\\neos earum sed incidunt fugiat. Minus quis iusto enim quod a. Autem sequi\\\\nreiciendis quibusdam et nostrum.</p>\\\\n<strong>Benefits and perks</strong>\\\\n<p>Possimus libero sit vitae et sed quidem. Eos eveniet quo est id iste sint.\\\\nNihil corporis in eos consequatur quas ut harum voluptates et. Deleniti minus\\\\nvoluptatem maiores consequatur est molestiae quo est cupiditate. Mollitia\\\\ndoloremque quam non. Omnis eum nihil nobis illo cum.</p>\\\\n<strong>Skills and experience</strong>\\\\n<p>Dolor quia beatae nulla perferendis culpa ea saepe ut. Excepturi non vitae\\\\nullam consequuntur accusantium sit iste asperiores. Porro quisquam dolor\\\\nquia. Natus ut sunt animi. Voluptatem minima perspiciatis maiores ut. Atque\\\\nad qui sed beatae odit non.</p>\\\\n\\\",\\n\\n  // => `seekHirerJobReference`\\n  \\\"jobReference\\\": \\\"REQ-65558\\\",\\n\\n  // Branding and search bullets are no longer tied to `standout`.\\n  \\\"standout\\\": {\\n    // => `postingInstructions[].brandingId`\\n    \\\"brandingId\\\": \\\"globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L\\\"\\n  },\\n\\n  // => `postingInstructions[].seekAnzAdvertisementType`\\n  \\\"advertisementType\\\": \\\"Classic\\\",\\n\\n  // => `postingInstructions[].end`\\n  \\\"expiryDate\\\": \\\"2038-01-01T00:00:00Z\\\",\\n\\n  // Use `postingInstructions[].start` & `postingInstructions[].end`.\\n  // If current date is within these ranges, the job ad is still open.\\n  \\\"state\\\": \\\"Open\\\",\\n\\n  \\\"_links\\\": {\\n    // GraphQL does not use HATEOAS\\n    \\\"self\\\": { \\\"href\\\": \\\"/advertisements/1c855f8d-86a9-44f4-b7ab-250e01ff8a77\\\" },\\n\\n    // => `positionUri`\\n    \\\"id\\\": {\\n      \\\"href\\\": \\\"/advertisements/1c855f8d-86a9-44f4-b7ab-250e01ff8a77/view\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":454,\"column\":1,\"offset\":26278},\"end\":{\"line\":543,\"column\":4,\"offset\":29956},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"http\",\"meta\":\"label": "\\\"Request\\\"\",\"value\":\"GET",
        "/advertisement/1c855f8d-86a9-44f4-b7ab-250e01ff8a77\\nHost:": true,
        "adposting-integration.cloud.seek.com.au\",\"position\":{\"start\":{\"line\":449,\"column\":1,\"offset\":26147},\"end\":{\"line\":452,\"column\":4,\"offset\":26276},\"indent\":[1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Response\\\"\",\"value\":\"{\\n",
        "": true,
        "//": true,
        "The": true,
        "SEEK": true,
        "API": true,
        "uses": true,
        "the": true,
        "object": true,
        "identifier": true,
        "in": true,
        "`profileId.value`": true,
        "to": true,
        "uniquely\\n": true,
        "identify": true,
        "job": true,
        "ads.": true,
        "However,": true,
        "these": true,
        "are": true,
        "distinct": true,
        "from": true,
        "Job": true,
        "Posting": true,
        "API's\\n": true,
        "advertisement": true,
        "IDs.\\n": true,
        "\\\"id\\\":": true,
        "\\\"1c855f8d-86a9-44f4-b7ab-250e01ff8a77\\\",\\n\\n": true,
        "\\\"thirdParties\\\":": true,
        "{\\n": true,
        "`positionOrganizations[].id.seekAnzAdvertiserId`\\n": true,
        "\\\"advertiserId\\\":": true,
        "\\\"10000\\\"\\n": true,
        "},\\n\\n": true,
        "`positionOpening.postingRequester.personContacts[]`\\n": true,
        "\\\"recruiter\\\":": true,
        "`positionOpening.postingRequester.personContacts[].name.formattedName`\\n": true,
        "\\\"fullName\\\":": true,
        "\\\"Lucy": true,
        "Flatley\\\",\\n\\n": true,
        "`positionOpening.postingRequester.personContacts[].communication.email[].address`\\n": true,
        "\\\"email\\\":": true,
        "\\\"Lucy_Flatley15@example.com\\\"\\n": true,
        "`positionTitle`\\n": true,
        "\\\"jobTitle\\\":": true,
        "\\\"Chief": true,
        "Assurance": true,
        "Analyst\\\",\\n\\n": true,
        "`positionLocation`\\n": true,
        "\\\"location\\\":": true,
        "\\\"Sydney\\\",\\n": true,
        "\\\"areaId\\\":": true,
        "\\\"CBDInnerWestEasternSuburbs\\\"\\n": true,
        "`jobCategories[]`\\n": true,
        "jobCategories": true,
        "requires": true,
        "an": true,
        "specify": true,
        "a": true,
        "category": true,
        "hierarchy.\\n": true,
        "\\\"subclassificationId\\\":": true,
        "\\\"TestingQualityAssurance\\\",\\n\\n": true,
        "`seekAnzWorkTypeCode`\\n": true,
        "\\\"workType\\\":": true,
        "\\\"PartTime\\\",\\n\\n": true,
        "`offeredRemunerationPackage`\\n": true,
        "\\\"salary\\\":": true,
        "`offeredRemunerationPackage.basisCode`\\n": true,
        "\\\"type\\\":": true,
        "\\\"HourlyRate\\\",\\n\\n": true,
        "`offeredRemunerationPackage.ranges[].minimumAmount.value`\\n": true,
        "\\\"minimum\\\":": true,
        "75,\\n\\n": true,
        "`offeredRemunerationPackage.ranges[].maximumAmount.value`\\n": true,
        "\\\"maximum\\\":": true,
        "99.99,\\n\\n": true,
        "`offeredRemunerationPackage.descriptions`\\n": true,
        "\\\"details\\\":": true,
        "\\\"$75": true,
        "-": true,
        "$100": true,
        "per": true,
        "hour\\\"\\n": true,
        "`positionFormattedDescriptions[]`\\n": true,
        "Formatted": true,
        "description": true,
        "should": true,
        "have": true,
        "descriptionId.value": true,
        "of": true,
        "\\\"SearchSummary\\\"\\n": true,
        "\\\"jobSummary\\\":": true,
        "\\\"Excellent": true,
        "opportunity": true,
        "for": true,
        "assurance": true,
        "analyst": true,
        "Australia\\\",\\n\\n": true,
        "\\\"AdvertisementDetails\\\"\\n": true,
        "\\\"advertisementDetails\\\":": true,
        "\\\"\\\\n<strong>About": true,
        "role</strong>\\\\n<p>Ut": true,
        "necessitatibus": true,
        "amet": true,
        "deserunt": true,
        "laudantium": true,
        "veritatis.": true,
        "Sapiente": true,
        "occaecati\\\\nodio": true,
        "quae": true,
        "quod": true,
        "magnam": true,
        "autem": true,
        "temporibus": true,
        "error": true,
        "rerum.": true,
        "Id": true,
        "porro": true,
        "distinctio": true,
        "eaque\\\\neos": true,
        "earum": true,
        "sed": true,
        "incidunt": true,
        "fugiat.": true,
        "Minus": true,
        "quis": true,
        "iusto": true,
        "enim": true,
        "a.": true,
        "Autem": true,
        "sequi\\\\nreiciendis": true,
        "quibusdam": true,
        "et": true,
        "nostrum.</p>\\\\n<strong>Benefits": true,
        "and": true,
        "perks</strong>\\\\n<p>Possimus": true,
        "libero": true,
        "sit": true,
        "vitae": true,
        "quidem.": true,
        "Eos": true,
        "eveniet": true,
        "quo": true,
        "est": true,
        "id": true,
        "iste": true,
        "sint.\\\\nNihil": true,
        "corporis": true,
        "eos": true,
        "consequatur": true,
        "quas": true,
        "ut": true,
        "harum": true,
        "voluptates": true,
        "et.": true,
        "Deleniti": true,
        "minus\\\\nvoluptatem": true,
        "maiores": true,
        "molestiae": true,
        "cupiditate.": true,
        "Mollitia\\\\ndoloremque": true,
        "quam": true,
        "non.": true,
        "Omnis": true,
        "eum": true,
        "nihil": true,
        "nobis": true,
        "illo": true,
        "cum.</p>\\\\n<strong>Skills": true,
        "experience</strong>\\\\n<p>Dolor": true,
        "quia": true,
        "beatae": true,
        "nulla": true,
        "perferendis": true,
        "culpa": true,
        "ea": true,
        "saepe": true,
        "ut.": true,
        "Excepturi": true,
        "non": true,
        "vitae\\\\nullam": true,
        "consequuntur": true,
        "accusantium": true,
        "asperiores.": true,
        "Porro": true,
        "quisquam": true,
        "dolor\\\\nquia.": true,
        "Natus": true,
        "sunt": true,
        "animi.": true,
        "Voluptatem": true,
        "minima": true,
        "perspiciatis": true,
        "Atque\\\\nad": true,
        "qui": true,
        "odit": true,
        "non.</p>\\\\n\\\",\\n\\n": true,
        "`seekHirerJobReference`\\n": true,
        "\\\"jobReference\\\":": true,
        "\\\"REQ-65558\\\",\\n\\n": true,
        "Branding": true,
        "search": true,
        "bullets": true,
        "no": true,
        "longer": true,
        "tied": true,
        "`standout`.\\n": true,
        "\\\"standout\\\":": true,
        "`postingInstructions[].brandingId`\\n": true,
        "\\\"brandingId\\\":": true,
        "\\\"globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L\\\"\\n": true,
        "`postingInstructions[].seekAnzAdvertisementType`\\n": true,
        "\\\"advertisementType\\\":": true,
        "\\\"Classic\\\",\\n\\n": true,
        "`postingInstructions[].end`\\n": true,
        "\\\"expiryDate\\\":": true,
        "\\\"2038-01-01T00:00:00Z\\\",\\n\\n": true,
        "Use": true,
        "`postingInstructions[].start`": true,
        "&": true,
        "`postingInstructions[].end`.\\n": true,
        "If": true,
        "current": true,
        "date": true,
        "is": true,
        "within": true,
        "ranges,": true,
        "ad": true,
        "still": true,
        "open.\\n": true,
        "\\\"state\\\":": true,
        "\\\"Open\\\",\\n\\n": true,
        "\\\"_links\\\":": true,
        "GraphQL": true,
        "does": true,
        "not": true,
        "use": true,
        "HATEOAS\\n": true,
        "\\\"self\\\":": true,
        "{": true,
        "\\\"href\\\":": true,
        "\\\"/advertisements/1c855f8d-86a9-44f4-b7ab-250e01ff8a77\\\"": true,
        "`positionUri`\\n": true,
        "\\\"/advertisements/1c855f8d-86a9-44f4-b7ab-250e01ff8a77/view\\\"\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":454,\"column\":1,\"offset\":26278},\"end\":{\"line\":543,\"column\":4,\"offset\":29956},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`GET /advertisement/1c855f8d-86a9-44f4-b7ab-250e01ff8a77
Host: adposting-integration.cloud.seek.com.au
`}</code></pre>
    <p><strong parentName="p">{`SEEK API`}</strong></p>
    <p>{`GraphQL `}<a parentName="p" {...{
        "href": "/schema/query/positionProfile"
      }}><inlineCode parentName="a">{`positionProfile`}</inlineCode>{` query`}</a>{` with a required `}<inlineCode parentName="p">{`id`}</inlineCode>{` input.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query ($id: String!) {\\n  positionProfile(id: $id) {\\n    profileId {\\n      value\\n    }\\n    positionOrganizations {\\n      id {\\n        value\\n      }\\n      name\\n      seekAnzAdvertiserId\\n    }\\n    positionOpening {\\n      postingRequester {\\n        personContacts {\\n          name {\\n            formattedName\\n          }\\n          communication {\\n            email {\\n              address\\n            }\\n          }\\n        }\\n      }\\n    }\\n    positionTitle\\n    positionLocation {\\n      id {\\n        value\\n      }\\n      name\\n    }\\n    jobCategories {\\n      id {\\n        value\\n      }\\n      name\\n    }\\n    positionFormattedDescriptions {\\n      descriptionId {\\n        value\\n      }\\n      content\\n    }\\n    seekHirerJobReference\\n    seekAnzWorkTypeCode\\n    offeredRemunerationPackage {\\n      basisCode\\n      ranges {\\n        minimumAmount {\\n          value\\n          currency\\n        }\\n        maximumAmount {\\n          value\\n          currency\\n        }\\n        intervalCode\\n      }\\n      descriptions\\n    }\\n    seekVideo {\\n      url\\n      seekAnzPositionCode\\n    }\\n    postingInstructions {\\n      seekAnzAdvertisementType\\n      applicationMethods {\\n        applicationUri {\\n          url\\n        }\\n      }\\n      brandingId\\n      start\\n      end\\n    }\\n    positionUri\\n  }\\n}\",\"position\":{\"start\":{\"line\":549,\"column\":1,\"offset\":30035},\"end\":{\"line\":630,\"column\":4,\"offset\":31352},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label=\\\"Variables\\\"\",\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n}\",\"position\":{\"start\":{\"line\":632,\"column\":1,\"offset\":31354},\"end\":{\"line\":636,\"column\":4,\"offset\":31447},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label=\\\"Response\\\"\",\"value\":\"{\\n  \\\"positionProfile\\\": {\\n    \\\"profileId\\\": {\\n      \\\"value\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n    },\\n    \\\"positionOrganizations\\\": [\\n      {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n        },\\n        \\\"name\\\": \\\"Acme Corp\\\",\\n        \\\"seekAnzAdvertiserId\\\": 10000\\n      }\\n    ],\\n    \\\"positionOpening\\\": {\\n      \\\"postingRequester\\\": {\\n        \\\"personContacts\\\": [\\n          {\\n            \\\"name\\\": {\\n              \\\"formattedName\\\": \\\"Lucy Flatley\\\"\\n            },\\n            \\\"communication\\\": {\\n              \\\"email\\\": [\\n                {\\n                  \\\"address\\\": \\\"Lucy_Flatley15@example.com\\\"\\n                }\\n              ]\\n            }\\n          }\\n        ]\\n      }\\n    },\\n    \\\"positionTitle\\\": \\\"Chief Assurance Analyst\\\",\\n    \\\"positionLocation\\\": [\\n      {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:location:seek:2vfkAqUFR\\\"\\n        },\\n        \\\"name\\\": \\\"Sydney Olympic Park\\\"\\n      }\\n    ],\\n    \\\"jobCategories\\\": [\\n      {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:2MEEKv9K5\\\"\\n        },\\n        \\\"name\\\": \\\"Testing & Quality Assurance\\\"\\n      }\\n    ],\\n    \\\"positionFormattedDescriptions\\\": [\\n      {\\n        \\\"descriptionId\\\": {\\n          \\\"value\\\": \\\"SearchSummary\\\"\\n        },\\n        \\\"content\\\": \\\"Excellent opportunity for an assurance analyst in Australia\\\"\\n      },\\n      {\\n        \\\"descriptionId\\\": {\\n          \\\"value\\\": \\\"AdvertisementDetails\\\"\\n        },\\n        \\\"content\\\": \\\"\\\\n<strong>About the role</strong>\\\\n<p>Ut necessitatibus amet deserunt laudantium veritatis. Sapiente occaecati\\\\nodio quae quod magnam autem temporibus error rerum. Id porro distinctio eaque\\\\neos earum sed incidunt fugiat. Minus quis iusto enim quod a. Autem sequi\\\\nreiciendis quibusdam et nostrum.</p>\\\\n<strong>Benefits and perks</strong>\\\\n<p>Possimus libero sit vitae et sed quidem. Eos eveniet quo est id iste sint.\\\\nNihil corporis in eos consequatur quas ut harum voluptates et. Deleniti minus\\\\nvoluptatem maiores consequatur est molestiae quo est cupiditate. Mollitia\\\\ndoloremque quam non. Omnis eum nihil nobis illo cum.</p>\\\\n<strong>Skills and experience</strong>\\\\n<p>Dolor quia beatae nulla perferendis culpa ea saepe ut. Excepturi non vitae\\\\nullam consequuntur accusantium sit iste asperiores. Porro quisquam dolor\\\\nquia. Natus ut sunt animi. Voluptatem minima perspiciatis maiores ut. Atque\\\\nad qui sed beatae odit non.</p>\\\\n\\\"\\n      }\\n    ],\\n    \\\"seekHirerJobReference\\\": \\\"REQ-65558\\\",\\n    \\\"seekAnzWorkTypeCode\\\": \\\"PartTime\\\",\\n    \\\"offeredRemunerationPackage\\\": {\\n      \\\"basisCode\\\": \\\"Hourly\\\",\\n      \\\"ranges\\\": [\\n        {\\n          \\\"minimumAmount\\\": {\\n            \\\"value\\\": 75,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"maximumAmount\\\": {\\n            \\\"value\\\": 99.99,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"intervalCode\\\": \\\"Hour\\\"\\n        }\\n      ],\\n      \\\"descriptions\\\": [\\\"$75 - $100 per hour\\\"]\\n    },\\n    \\\"seekVideo\\\": null,\\n    \\\"postingInstructions\\\": [\\n      {\\n        \\\"seekAnzAdvertisementType\\\": \\\"Classic\\\",\\n        \\\"applicationMethods\\\": [],\\n        \\\"brandingId\\\": \\\"globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L\\\",\\n        \\\"start\\\": \\\"2019-08-20T21:02:27.101Z\\\",\\n        \\\"end\\\": \\\"2038-01-01T00:00:00.000Z\\\"\\n      }\\n    ],\\n    \\\"positionUri\\\": \\\"https://example.seek.com.au/job/30000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":638,\"column\":1,\"offset\":31449},\"end\":{\"line\":734,\"column\":4,\"offset\":34768},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "positionProfile(id:": true,
        "$id)": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "positionOrganizations": true,
        "id": true,
        "name\\n": true,
        "seekAnzAdvertiserId\\n": true,
        "positionOpening": true,
        "postingRequester": true,
        "personContacts": true,
        "name": true,
        "formattedName\\n": true,
        "communication": true,
        "email": true,
        "address\\n": true,
        "positionTitle\\n": true,
        "positionLocation": true,
        "jobCategories": true,
        "positionFormattedDescriptions": true,
        "descriptionId": true,
        "content\\n": true,
        "seekHirerJobReference\\n": true,
        "seekAnzWorkTypeCode\\n": true,
        "offeredRemunerationPackage": true,
        "basisCode\\n": true,
        "ranges": true,
        "minimumAmount": true,
        "currency\\n": true,
        "maximumAmount": true,
        "intervalCode\\n": true,
        "descriptions\\n": true,
        "seekVideo": true,
        "url\\n": true,
        "seekAnzPositionCode\\n": true,
        "postingInstructions": true,
        "seekAnzAdvertisementType\\n": true,
        "applicationMethods": true,
        "applicationUri": true,
        "brandingId\\n": true,
        "start\\n": true,
        "end\\n": true,
        "positionUri\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":549,\"column\":1,\"offset\":30035},\"end\":{\"line\":630,\"column\":4,\"offset\":31352},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label": "\\\"Variables\\\"\",\"value\":\"{\\n",
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n}\",\"position\":{\"start\":{\"line\":632,\"column\":1,\"offset\":31354},\"end\":{\"line\":636,\"column\":4,\"offset\":31447},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label": "\\\"Response\\\"\",\"value\":\"{\\n",
        "\\\"positionProfile\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n": true,
        "},\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "[\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "\\\"name\\\":": true,
        "\\\"Acme": true,
        "Corp\\\",\\n": true,
        "\\\"seekAnzAdvertiserId\\\":": true,
        "10000\\n": true,
        "],\\n": true,
        "\\\"positionOpening\\\":": true,
        "\\\"postingRequester\\\":": true,
        "\\\"personContacts\\\":": true,
        "\\\"formattedName\\\":": true,
        "\\\"Lucy": true,
        "Flatley\\\"\\n": true,
        "\\\"communication\\\":": true,
        "\\\"email\\\":": true,
        "\\\"address\\\":": true,
        "\\\"Lucy_Flatley15@example.com\\\"\\n": true,
        "]\\n": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Chief": true,
        "Assurance": true,
        "Analyst\\\",\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:2vfkAqUFR\\\"\\n": true,
        "\\\"Sydney": true,
        "Olympic": true,
        "Park\\\"\\n": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2MEEKv9K5\\\"\\n": true,
        "\\\"Testing": true,
        "&": true,
        "Quality": true,
        "Assurance\\\"\\n": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "\\\"descriptionId\\\":": true,
        "\\\"SearchSummary\\\"\\n": true,
        "\\\"content\\\":": true,
        "\\\"Excellent": true,
        "opportunity": true,
        "for": true,
        "an": true,
        "assurance": true,
        "analyst": true,
        "in": true,
        "Australia\\\"\\n": true,
        "\\\"AdvertisementDetails\\\"\\n": true,
        "\\\"\\\\n<strong>About": true,
        "the": true,
        "role</strong>\\\\n<p>Ut": true,
        "necessitatibus": true,
        "amet": true,
        "deserunt": true,
        "laudantium": true,
        "veritatis.": true,
        "Sapiente": true,
        "occaecati\\\\nodio": true,
        "quae": true,
        "quod": true,
        "magnam": true,
        "autem": true,
        "temporibus": true,
        "error": true,
        "rerum.": true,
        "Id": true,
        "porro": true,
        "distinctio": true,
        "eaque\\\\neos": true,
        "earum": true,
        "sed": true,
        "incidunt": true,
        "fugiat.": true,
        "Minus": true,
        "quis": true,
        "iusto": true,
        "enim": true,
        "a.": true,
        "Autem": true,
        "sequi\\\\nreiciendis": true,
        "quibusdam": true,
        "et": true,
        "nostrum.</p>\\\\n<strong>Benefits": true,
        "and": true,
        "perks</strong>\\\\n<p>Possimus": true,
        "libero": true,
        "sit": true,
        "vitae": true,
        "quidem.": true,
        "Eos": true,
        "eveniet": true,
        "quo": true,
        "est": true,
        "iste": true,
        "sint.\\\\nNihil": true,
        "corporis": true,
        "eos": true,
        "consequatur": true,
        "quas": true,
        "ut": true,
        "harum": true,
        "voluptates": true,
        "et.": true,
        "Deleniti": true,
        "minus\\\\nvoluptatem": true,
        "maiores": true,
        "molestiae": true,
        "cupiditate.": true,
        "Mollitia\\\\ndoloremque": true,
        "quam": true,
        "non.": true,
        "Omnis": true,
        "eum": true,
        "nihil": true,
        "nobis": true,
        "illo": true,
        "cum.</p>\\\\n<strong>Skills": true,
        "experience</strong>\\\\n<p>Dolor": true,
        "quia": true,
        "beatae": true,
        "nulla": true,
        "perferendis": true,
        "culpa": true,
        "ea": true,
        "saepe": true,
        "ut.": true,
        "Excepturi": true,
        "non": true,
        "vitae\\\\nullam": true,
        "consequuntur": true,
        "accusantium": true,
        "asperiores.": true,
        "Porro": true,
        "quisquam": true,
        "dolor\\\\nquia.": true,
        "Natus": true,
        "sunt": true,
        "animi.": true,
        "Voluptatem": true,
        "minima": true,
        "perspiciatis": true,
        "Atque\\\\nad": true,
        "qui": true,
        "odit": true,
        "non.</p>\\\\n\\\"\\n": true,
        "\\\"seekHirerJobReference\\\":": true,
        "\\\"REQ-65558\\\",\\n": true,
        "\\\"seekAnzWorkTypeCode\\\":": true,
        "\\\"PartTime\\\",\\n": true,
        "\\\"offeredRemunerationPackage\\\":": true,
        "\\\"basisCode\\\":": true,
        "\\\"Hourly\\\",\\n": true,
        "\\\"ranges\\\":": true,
        "\\\"minimumAmount\\\":": true,
        "75,\\n": true,
        "\\\"currency\\\":": true,
        "\\\"AUD\\\"\\n": true,
        "\\\"maximumAmount\\\":": true,
        "99.99,\\n": true,
        "\\\"intervalCode\\\":": true,
        "\\\"Hour\\\"\\n": true,
        "\\\"descriptions\\\":": true,
        "[\\\"$75": true,
        "-": true,
        "$100": true,
        "per": true,
        "hour\\\"]\\n": true,
        "\\\"seekVideo\\\":": true,
        "null,\\n": true,
        "\\\"postingInstructions\\\":": true,
        "\\\"seekAnzAdvertisementType\\\":": true,
        "\\\"Classic\\\",\\n": true,
        "\\\"applicationMethods\\\":": true,
        "[],\\n": true,
        "\\\"brandingId\\\":": true,
        "\\\"globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L\\\",\\n": true,
        "\\\"start\\\":": true,
        "\\\"2019-08-20T21:02:27.101Z\\\",\\n": true,
        "\\\"end\\\":": true,
        "\\\"2038-01-01T00:00:00.000Z\\\"\\n": true,
        "\\\"positionUri\\\":": true,
        "\\\"https://example.seek.com.au/job/30000000\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":638,\"column\":1,\"offset\":31449},\"end\":{\"line\":734,\"column\":4,\"offset\":34768},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  positionProfile(id: $id) {
    profileId {
      value
    }
    positionOrganizations {
      id {
        value
      }
      name
      seekAnzAdvertiserId
    }
    positionOpening {
      postingRequester {
        personContacts {
          name {
            formattedName
          }
          communication {
            email {
              address
            }
          }
        }
      }
    }
    positionTitle
    positionLocation {
      id {
        value
      }
      name
    }
    jobCategories {
      id {
        value
      }
      name
    }
    positionFormattedDescriptions {
      descriptionId {
        value
      }
      content
    }
    seekHirerJobReference
    seekAnzWorkTypeCode
    offeredRemunerationPackage {
      basisCode
      ranges {
        minimumAmount {
          value
          currency
        }
        maximumAmount {
          value
          currency
        }
        intervalCode
      }
      descriptions
    }
    seekVideo {
      url
      seekAnzPositionCode
    }
    postingInstructions {
      seekAnzAdvertisementType
      applicationMethods {
        applicationUri {
          url
        }
      }
      brandingId
      start
      end
    }
    positionUri
  }
}
`}</code></pre>
    <p>{`For more general information, see the documentation on `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/querying-a-job-ad"
      }}>{`querying a job ad`}</a>{` in the Job Posting use case.`}</p>
    <h3 {...{
      "id": "get-all-advertisements"
    }}>{`Get all advertisements`}</h3>
    <p><strong parentName="p">{`Job Posting API`}</strong></p>
    <p><inlineCode parentName="p">{`GET`}</inlineCode>{` to `}<inlineCode parentName="p">{`/advertisement`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http",
        "metastring": "label=\"Request\"",
        "label": "\"Request\""
      }}>{`GET /advertisement
Host: adposting-integration.cloud.seek.com.au
`}</code></pre>
    <p><strong parentName="p">{`SEEK API`}</strong></p>
    <p>{`It’s not possible to enumerate all advertisements for an integration partner in the SEEK API.
You can only get all advertisements for a particular hirer using `}<a parentName="p" {...{
        "href": "/schema/query/positionOpenings"
      }}><inlineCode parentName="a">{`positionOpenings`}</inlineCode>{` query`}</a>{`.`}</p>
    <p>{`You can also be notified when a job ad goes live with the `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionProfilePostedEvent"
      }}><inlineCode parentName="a">{`PositionProfilePosted`}</inlineCode>{` event`}</a>{`, which is detailed in the next phase.`}</p>
    <h3 {...{
      "id": "get-all-advertisements-for-an-advertiser"
    }}>{`Get all advertisements for an advertiser`}</h3>
    <p><strong parentName="p">{`Job Posting API`}</strong></p>
    <p><inlineCode parentName="p">{`GET`}</inlineCode>{` to `}<inlineCode parentName="p">{`/advertisement?advertiserId={advertiserID}`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http",
        "metastring": "label=\"Request\"",
        "label": "\"Request\""
      }}>{`GET /advertisement?advertiserId=10000
Host: adposting-integration.cloud.seek.com.au
`}</code></pre>
    <p><strong parentName="p">{`SEEK API`}</strong></p>
    <p>{`GraphQL `}<a parentName="p" {...{
        "href": "/schema/query/positionOpenings"
      }}><inlineCode parentName="a">{`positionOpenings`}</inlineCode>{` query`}</a>{` with a required `}<inlineCode parentName="p">{`hirerId`}</inlineCode>{` input.
The `}<inlineCode parentName="p">{`positionOpenings`}</inlineCode>{` query returns a paginated list of `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionOpening"
      }}><inlineCode parentName="a">{`PositionOpening`}</inlineCode></a>{` objects.
You can select fields from its nested `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionOpening#/field/paginatedPositionProfiles"
      }}><inlineCode parentName="a">{`PositionOpening.paginatedPositionProfiles`}</inlineCode></a>{` to retrieve information about a subset of its job ads.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($hirerId: String!) {\\n  positionOpenings(hirerId: $hirerId) {\\n    edges {\\n      node {\\n        statusCode\\n        paginatedPositionProfiles {\\n          edges {\\n            node {\\n              profileId {\\n                value\\n              }\\n            }\\n          }\\n          pageInfo {\\n            hasNextPage\\n            endCursor\\n          }\\n        }\\n      }\\n    }\\n    pageInfo {\\n      hasNextPage\\n      endCursor\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":778,\"column\":1,\"offset\":36138},\"end\":{\"line\":805,\"column\":4,\"offset\":36591},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":807,\"column\":1,\"offset\":36593},\"end\":{\"line\":811,\"column\":4,\"offset\":36668},\"indent\":[1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($hirerId:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "positionOpenings(hirerId:": true,
        "$hirerId)": true,
        "edges": true,
        "node": true,
        "statusCode\\n": true,
        "paginatedPositionProfiles": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "pageInfo": true,
        "hasNextPage\\n": true,
        "endCursor\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":778,\"column\":1,\"offset\":36138},\"end\":{\"line\":805,\"column\":4,\"offset\":36591},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":807,\"column\":1,\"offset\":36593},\"end\":{\"line\":811,\"column\":4,\"offset\":36668},\"indent\":[1,1,1,1]}}]": true
      }}>{`query ($hirerId: String!) {
  positionOpenings(hirerId: $hirerId) {
    edges {
      node {
        statusCode
        paginatedPositionProfiles {
          edges {
            node {
              profileId {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
`}</code></pre>
    <p>{`For more general information, see the documentation on `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/position-openings"
      }}>{`position openings`}</a>{` in the Job Posting use case.`}</p>
    <h2 {...{
      "id": "exit-criteria"
    }}>{`Exit criteria`}</h2>
    <p>{`In order to move to the next phase, you will need to have completed the following:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><p parentName="th"><strong parentName="p">{`Criteria`}</strong></p></th>
          <th parentName="tr" {...{
            "align": null
          }}><p parentName="th"><strong parentName="p">{`Description`}</strong></p></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Existing integration maintained`}</p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Hirer can continue to manage jobs posted via the Job Posting API`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Job ad posted successfully`}</p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Hirer can successfully post a job ad via the SEEK API`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Job ad updated successfully`}</p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Hirer can successfully update a job ad via the SEEK API`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Job ad closed successfully`}</p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Hirer can successfully close a job ad via the SEEK API`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Job ad viewed successfully`}</p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Hirer can view job ad details via the SEEK API`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Job ads listed successfully`}</p></td>
          <td parentName="tr" {...{
            "align": null
          }}><p parentName="td">{`Hirer can see a list of their job ads posted via the SEEK API`}</p></td>
        </tr>
      </tbody>
    </table>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;